import { IconButton } from "@/src/components/buttons";
import { openDrawer } from "@/src/components/dialogs";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import SearchKeyHighliter from "@/src/components/seachkeyHighlighter";
import { Input } from "@/src/components/universalInput";
import { UploadSpots } from "@/src/components/uploadSpots";
import { Discussion, Message, Upload, User } from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  useSubscribeToTable,
  useSubscribeToTableWithPolls,
} from "@/src/utils/socket";
import {
  debounce,
  fromNow,
  getDbType,
  getHospitalId,
  getImgUrl,
  getItemId,
  getServiceId,
  getUserId,
  getUserShortName,
  isHospital,
  isMobile,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ImAttachment } from "react-icons/im";
import { LuSendHorizonal } from "react-icons/lu";
import { UserAvatar } from "../admin/users/userAvatar";
import { UniversalItem } from "../patients/universalItem";
import moment from "moment";

function MessageriePage() {
  const [discussion, setDiscussion] = useState<Discussion | null | undefined>(
    null
  );

  return (
    <div id="messageriePage" className="page">
      <MessagerieUsersList
        hospitalId={getHospitalId()}
        serviceId={getServiceId()}
        currentDiscussion={discussion}
        onSelect={async (newValue) => {
          let discussion = newValue;
          setDiscussion(discussion);
          if (isMobile())
            await openDiscussionDrawer({
              interlocutor: null,
              attachedItem: null,
              discussion,
            });
        }}
      ></MessagerieUsersList>

      {!isMobile() && (
        <MessagesSection
          showHeader={true}
          discussion={discussion}
        ></MessagesSection>
      )}
    </div>
  );
}

function MessagerieUsersList({
  currentDiscussion,
  hospitalId,
  serviceId,
  roleId,
  onSelect,
}: {
  currentDiscussion?: Discussion | null | undefined;
  hospitalId?: string | null | undefined;
  serviceId?: string | null | undefined;
  roleId?: string | null | undefined;
  onSelect: (discussion: Discussion) => void;
}) {
  const [reload, setReload] = useState(0);
  let [selectedDiscussion, setSelectedDiscussion] = useState(currentDiscussion);
  let [searchKey, setSearchKey] = useState("");

  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "MessagerieUsersList",
      searchKey,
      hospitalId,
      serviceId,
      roleId,
      reload,
    ],
    queryFn: async () =>
      apiFetch("users", "loadMessagerieUsers", {
        searchKey,
        serviceId: isHospital() ? null : getItemId(USER.get()?.service),
        userId: getUserId(),
      }),
  });

  function getDiscussionUsers() {
    let users: Record<string, User> = {};
    for (let discussion of data?.discussions || []) {
      if (discussion.users?.length != 2) continue;
      for (let user of discussion.users) {
        if (!user) continue;
        users[getItemId(user)] = user;
      }
    }
    return users;
  }

  let existingDiscussionUsers = getDiscussionUsers();

  return (
    <div
      className=" p10 gap10 white-bg"
      style={{ height: "100vh", overflowY: "scroll" }}
    >
      <ListConstructor
        data={[...(data?.users || []), ...(data?.discussions || [])]}
        isLoading={isLoading}
        error={error}
        onSearch={(searchKey) => {
          setSearchKey(searchKey || "");
        }}
        searchKey={searchKey}
      >
        <div className={`messagerieUsersList grid c1 gap10`}>
          {data?.discussions?.map((discussion) => {
            return (
              <DiscussionsListItem
                key={uid()}
                discussion={discussion}
                searchKey={searchKey}
                isSelected={selectedDiscussion?.id === discussion.id}
                onSelect={() => {
                  setSelectedDiscussion(discussion);
                  onSelect(discussion);
                }}
              ></DiscussionsListItem>
            );
          })}
          {data?.users?.map((user) => {
            if (!user.service?.name) return null;
            if (existingDiscussionUsers[getItemId(user)] != null) return null;
            return (
              <MessagerieUser
                key={uid()}
                user={user}
                searchKey={searchKey}
                isSelected={
                  selectedDiscussion?.users?.find((discussionUser) => {
                    return getItemId(discussionUser) == getItemId(user);
                  }) != null
                }
                onSelect={async () => {
                  let discussion: Discussion = {
                    name: null,
                    creator: getUserId(),
                    lastMessage: null,
                    status: "pending",
                    type: "simple",
                    users: [getUserId(), getItemId(user)],
                  };

                  discussion = await apiFetch("discussions", "addOne", {
                    discussion,
                  });

                  setSelectedDiscussion(discussion);
                  onSelect(discussion);
                  // setReload(reload + 1);
                }}
              ></MessagerieUser>
            );
          })}
        </div>
      </ListConstructor>
    </div>
  );
}

function DiscussionsListItem({
  discussion,
  searchKey,
  isSelected,
  onSelect,
}: {
  discussion: Discussion;
  isSelected: boolean;
  searchKey?: string;
  onSelect?: (discussion: Discussion) => void;
}) {
  let isGroup = discussion.users.length > 2;

  if (isGroup) {
    return (
      <div
        className={`messagerieUser ${isSelected ? "active" : ""}`}
        onClick={async () => {
          onSelect?.(discussion);
        }}
      >
        <div>Groupe</div>
        <div>
          <div className="bold blue-color">
            {discussion.name ||
              discussion.users
                .map((interlocutor) => {
                  getUserShortName(interlocutor);
                })
                .join(", ")}
          </div>
          <LastMessageText
            message={discussion.lastMessage}
            isSelected={isSelected}
          ></LastMessageText>
        </div>
      </div>
    );
  }

  let interlocutor = getInterlocutor(discussion.users);
  return (
    <div
      className={`messagerieUser  acenter ${isSelected ? "active" : ""}`}
      onClick={async () => {
        onSelect?.(discussion);
      }}
    >
      <UserAvatar user={interlocutor} size={40}></UserAvatar>
      <div className="c1">
        <div className="bold blue-color">
          <SearchKeyHighliter searchKey={searchKey}>
            {getUserShortName(interlocutor)}
          </SearchKeyHighliter>
        </div>
        <LastMessageText
          message={discussion.lastMessage}
          isSelected={isSelected}
        ></LastMessageText>
      </div>
    </div>
  );
}

function LastMessageText({
  message,
  isSelected,
}: {
  message: Message | null | undefined;
  isSelected: boolean;
}) {
  if (!message) return null;
  return (
    <div className={` c1 gap5 `}>
      <div
        className={`${
          message.readBy?.includes(getUserId()) || isSelected ? "gray" : "bold"
        }`}
      >
        {message.content}
      </div>
      <div className="gray">{fromNow(message._date)}</div>
    </div>
  );
}

function MessagerieUser({
  user,
  isSelected,
  searchKey,
  onSelect,
}: {
  user: User;
  isSelected?: boolean;
  searchKey?: string;
  onSelect?: (user: User) => void;
}) {
  return (
    <div
      className={`messagerieUser ${isSelected ? "active" : ""}`}
      onClick={() => {
        onSelect?.(user);
      }}
    >
      <UserAvatar user={user} size={40}></UserAvatar>
      <div className="c1 gap5">
        <div className="bold ">
          <SearchKeyHighliter searchKey={searchKey}>
            {getUserShortName(user)}
          </SearchKeyHighliter>
        </div>
        <div className="gray">
          {user.service?.name} - {user.service?.hospital?.name}
        </div>
      </div>
    </div>
  );
}

function MessagesSection({
  currentAttachedItem,
  discussion,
  showHeader,
}: {
  currentAttachedItem?: any;
  discussion: Discussion | null | undefined;
  showHeader: boolean;
}) {
  const [attachedItem, setAttachedItem] = useState<any>(currentAttachedItem);
  const [showAttachments, setShowAttachments] = useState(false);
  const [reload, setReload] = useState(0);
  const [refreshInput, setRefreshInput] = useState(0);
  const [content, setContent] = useState<string>("");
  const [uploads, setuploads] = useState<Upload[]>([]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["messagesList", discussion?.id, reload],
    queryFn: async () =>
      apiFetch("messages", "loadMany", {
        discussionId: discussion?.id,
        userId: USER?.get()?.id,
      }),
  });

  function getLastModifiedDate() {
    let lastModifiedDate = moment().format("YYYY-MM-DD 00:00:00");
    for (let message of data || []) {
      let modifiedDate = message._modifiedDate;
      if (!modifiedDate) continue;

      if (moment(modifiedDate).isBefore(lastModifiedDate)) continue;
      lastModifiedDate = modifiedDate;
    }
    return lastModifiedDate;
  }

  useSubscribeToTableWithPolls({
    tableId: "discussions",
    widgetId: `discussions${discussion?.id}`,
    where:
      getDbType() == "sqlite"
        ? ` id = '${getItemId(discussion) || "123456789"}'  `
        : ` id = ${getItemId(discussion) || "discussions:123456789"}  `,
    lastModifiedDate: getLastModifiedDate(),
    intervalDurationInMilliseconds: 5000,
    callback: async (data) => {
      setReload(reload + 1);
    },
  });

  if (!discussion)
    return (
      <div className="gray-bg center p20 gray">
        Veuillez selectionner un utilisateur
      </div>
    );

  return (
    <div className="messagesSection">
      {showHeader ? (
        <MessagerieHeader discussion={discussion}></MessagerieHeader>
      ) : (
        <div></div>
      )}
      <div className="content">
        <ListConstructor
          key={reload}
          data={data || []}
          isLoading={isLoading}
          error={error}
        >
          <div className={`grid c1 gap10`}>
            {data?.map((message, index) => {
              return <MessageBubble key={uid()} message={message} />;
            })}
          </div>
        </ListConstructor>
      </div>
      <div className="footer c1-auto acenter">
        {showAttachments && (
          <div className="triple">
            <UploadSpots
              key={`  uploadSpots${discussion?.id}${refreshInput}`}
              gridCount={3}
              value={uploads}
              onChange={(newValue) => {
                setuploads(newValue);
              }}
            ></UploadSpots>
          </div>
        )}
        <Input
          key={`messageInput${discussion?.id}${refreshInput}`}
          value={content}
          name={null}
          type="textarea"
          rows={2}
          placeholder="Ecrire un message..."
          onChange={(newValue) => {
            setContent(newValue || "");
          }}
        />
        <IconButton
          isSecondary={true}
          onClick={async () => {
            setShowAttachments(!showAttachments);
          }}
        >
          <ImAttachment size={16} />
        </IconButton>
        <IconButton
          isMain={true}
          onClick={async () => {
            console.log("attachedItem", attachedItem);
            await apiFetch("messages", "addOne", {
              message: {
                readBy: [],
                content,
                uploads: uploads,
                discussion: getItemId(discussion),
                item: getItemId(attachedItem),
                creator: getUserId(),
              },
            });
            setContent("");
            setRefreshInput(refreshInput + 1);
            setAttachedItem(null);
            setuploads([]);
            setReload(reload + 1);
          }}
        >
          <LuSendHorizonal size={18} />
        </IconButton>
      </div>
    </div>
  );
}

function MessagerieHeader({
  discussion,
}: {
  discussion: Discussion | null | undefined;
}) {
  if (!discussion) return null;
  if (!discussion.users?.length)
    return (
      <div className="header">
        <div></div>
        <div>Aucun utilisateur trouvé</div>
      </div>
    );

  let isGroup = discussion.users.length > 2;

  if (isGroup) {
    return (
      <div className="messagerieHeader">
        <div>Groupe</div>
        <div>
          <div className="bold blue-color">
            {discussion.name ||
              discussion.users
                .map((interlocutor) => {
                  getUserShortName(interlocutor);
                })
                .join(", ")}
          </div>
        </div>
      </div>
    );
  }

  let interlocutor = getInterlocutor(discussion.users);

  return (
    <div className="messagerieHeader">
      <UserAvatar user={interlocutor} size={40}></UserAvatar>
      <div>
        <div className="bold blue-color">{getUserShortName(interlocutor)}</div>
        <div className="gray">{interlocutor?.email}</div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  let isSent = getItemId(USER?.get()) == getItemId(message?.creator);
  return (
    <div key={uid()} className={`message ${isSent ? "sent" : ""}`}>
      <div>
        {!isSent ? (
          <UserAvatar user={message.creator} size={30} />
        ) : (
          <div></div>
        )}
        <div key={uid()} className="messageBubble">
          <UniversalItem value={message.item}></UniversalItem>
          <div>{message.content}</div>
          <div className="c4 gap10">
            {message.uploads?.map((upload) => {
              return (
                <div key={uid()} className="attachment" onClick={() => {}}>
                  <img src={getImgUrl(upload)} alt={upload?.name} />
                </div>
              );
            })}
          </div>
        </div>
        {isSent ? <UserAvatar user={message.creator} size={30} /> : <div></div>}
      </div>
    </div>
  );
}

async function openDiscussionDrawer({
  interlocutor,
  discussion,
  attachedItem,
}: {
  interlocutor: User | null | undefined;
  discussion?: Discussion;
  attachedItem: any;
}) {
  if (!interlocutor && !discussion) throw new Error("Aucun utilisateur trouvé");
  discussion ||= {
    name: null,
    creator: getUserId(),
    lastMessage: null,
    status: "pending",
    type: "simple",
    users: [getUserId(), getItemId(interlocutor)],
  };

  discussion = await apiFetch("discussions", "addOne", {
    discussion,
  });

  return await openDrawer({
    id: "userMessagesDrawer",
    additionalClasses: ["noPadding"],
    title: <MessagerieHeader discussion={discussion}></MessagerieHeader>,
    content: (
      <MessagesSection
        discussion={discussion}
        showHeader={false}
        currentAttachedItem={attachedItem}
      ></MessagesSection>
    ),
  });
}

function getInterlocutor(
  discussionUsers: (User | null | undefined)[] | null | undefined
) {
  if (!discussionUsers) return null;

  let interlocutors = discussionUsers.filter((user) => {
    return getItemId(user) != getUserId();
  });

  if (interlocutors.length == 0 && discussionUsers.length != 0)
    interlocutors = [discussionUsers[0]];

  return interlocutors[0];
}

export {
  getInterlocutor,
  LastMessageText,
  MessagerieHeader,
  MessageriePage,
  MessagesSection,
  openDiscussionDrawer,
};
